import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useToast } from '@/shared/toast';
import { useAuth } from '@/shared/AuthContext';
import type { Hydrant } from './types';

/**
 * Modal "Scheda Idrante" (21 campi): localizzazione + caratteristiche +
 * stato + controllo + note. PUT /hydrants/{id}/scheda al salvataggio.
 * Include anche export PDF (apre nuova tab con HTML stampabile).
 */
export function HydrantSchedaModal({ hydrant, onClose }: { hydrant: Hydrant; onClose: () => void }): React.JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();

  type TriBool = '' | 'true' | 'false';
  const triFromBool = (b: boolean | undefined | null): TriBool => b === true ? 'true' : b === false ? 'false' : '';
  const triToBool = (t: TriBool): boolean | null => t === 'true' ? true : t === 'false' ? false : null;

  const { user } = useAuth();
  const userName = user ? ((user.fullName as string | undefined) ?? user.email ?? '') : '';

  const [form, setForm] = useState({
    code: hydrant.code ?? '',
    municipality: hydrant.municipality ?? '',
    street: hydrant.street ?? '',
    civicNumber: hydrant.civicNumber ?? '',
    compilerName: hydrant.compilerName ?? userName,
    type: hydrant.type ?? 'Soprasuolo',
    uniDiameter: hydrant.uniDiameter?.toString() ?? '',
    resource: hydrant.resource ?? '',
    efficient: triFromBool(hydrant.efficient),
    visualGood: triFromBool(hydrant.visualGood),
    valvePresent: triFromBool(hydrant.valvePresent),
    signagePresent: triFromBool(hydrant.signagePresent),
    pressureBar: hydrant.pressureBar?.toString() ?? '',
    diameterMm: hydrant.diameterMm?.toString() ?? '',
    accessibilityGood: triFromBool(hydrant.accessibilityGood),
    accessibilityNotes: hydrant.accessibilityNotes ?? '',
    photoTaken: triFromBool(hydrant.photoTaken),
    notes: hydrant.notes ?? '',
  });

  const save = useMutation({
    mutationFn: async () => {
      await api.put(`/hydrants/${hydrant.id}/scheda`, {
        code: form.code || null,
        municipality: form.municipality || null,
        street: form.street || null,
        civicNumber: form.civicNumber || null,
        compilerName: form.compilerName || null,
        type: form.type || null,
        uniDiameter: form.uniDiameter ? parseInt(form.uniDiameter, 10) : null,
        resource: form.resource || null,
        efficient: triToBool(form.efficient),
        visualGood: triToBool(form.visualGood),
        valvePresent: triToBool(form.valvePresent),
        signagePresent: triToBool(form.signagePresent),
        pressureBar: form.pressureBar ? parseFloat(form.pressureBar) : null,
        diameterMm: form.diameterMm ? parseFloat(form.diameterMm) : null,
        accessibilityGood: triToBool(form.accessibilityGood),
        accessibilityNotes: form.accessibilityNotes || null,
        photoTaken: triToBool(form.photoTaken),
        notes: form.notes || null,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hydrants'] });
      toast.show('Scheda salvata', 'success');
      onClose();
    },
    onError: () => toast.show('Errore salvataggio scheda', 'error'),
  });

  function exportPdf(): void {
    const safe = (s: string | null | undefined) => (s ?? '').replace(/[<>&"]/g, c =>
      c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '&' ? '&amp;' : '&quot;');
    const bool = (t: TriBool, yes: string, no: string) => t === 'true' ? yes : t === 'false' ? no : '—';
    const lastCheck = hydrant.lastInspectionAt
      ? new Date(hydrant.lastInspectionAt).toLocaleDateString('it-IT')
      : hydrant.createdAt ? new Date(hydrant.createdAt).toLocaleDateString('it-IT') : '—';

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Scheda Idrante ${safe(form.code)}</title>
<style>
  @page { margin: 18mm; }
  body { font-family: Arial, sans-serif; color: #222; }
  h1 { color: #dc3545; border-bottom: 3px solid #dc3545; padding-bottom: 6px; margin: 0 0 16px; }
  h2 { color: #444; margin-top: 22px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th, td { border: 1px solid #bbb; padding: 6px 10px; font-size: 13px; text-align: left; vertical-align: top; }
  th { background: #f5f5f5; width: 35%; }
  .footer { margin-top: 30px; font-size: 11px; color: #777; border-top: 1px solid #ccc; padding-top: 8px; }
  img.photo { max-width: 350px; max-height: 250px; border: 1px solid #aaa; margin: 8px 0; }
</style></head><body>
<h1>Scheda Idrante ${safe(form.code) || '(senza codice)'}</h1>

<h2>Localizzazione</h2>
<table>
  <tr><th>Codice idrante</th><td>${safe(form.code)}</td></tr>
  <tr><th>Comune</th><td>${safe(form.municipality)}</td></tr>
  <tr><th>Via</th><td>${safe(form.street)}</td></tr>
  <tr><th>Numero civico</th><td>${safe(form.civicNumber)}</td></tr>
  <tr><th>Latitudine</th><td>${hydrant.latitude.toFixed(6)}</td></tr>
  <tr><th>Longitudine</th><td>${hydrant.longitude.toFixed(6)}</td></tr>
</table>

<h2>Caratteristiche</h2>
<table>
  <tr><th>Tipo idrante</th><td>${safe(form.type)}</td></tr>
  <tr><th>Diametro UNI</th><td>${safe(form.uniDiameter)}</td></tr>
  <tr><th>Diametro (mm)</th><td>${safe(form.diameterMm)}</td></tr>
  <tr><th>Pressione (bar)</th><td>${safe(form.pressureBar)}</td></tr>
  <tr><th>Risorsa idrica</th><td>${safe(form.resource)}</td></tr>
</table>

<h2>Stato</h2>
<table>
  <tr><th>Stato efficienza</th><td>${bool(form.efficient, 'Funzionante', 'Non funzionante')}</td></tr>
  <tr><th>Stato visivo</th><td>${bool(form.visualGood, 'Buono', 'Non buono')}</td></tr>
  <tr><th>Valvola di chiusura</th><td>${bool(form.valvePresent, 'Presente', 'Non presente')}</td></tr>
  <tr><th>Cartello segnaletico</th><td>${bool(form.signagePresent, 'Presente', 'Non presente')}</td></tr>
  <tr><th>Accessibilità mezzi APS/ABP (min 2,5mt)</th><td>${bool(form.accessibilityGood, 'Buona', 'Non buona')}</td></tr>
  <tr><th>Note accessibilità</th><td>${safe(form.accessibilityNotes)}</td></tr>
  <tr><th>Fotografia</th><td>${bool(form.photoTaken, 'Eseguita', 'Non eseguita')}</td></tr>
</table>

${hydrant.photoPath && !/^https?:\/\//i.test(hydrant.photoPath) ? `<h2>Foto</h2><img class="photo" src="${window.location.origin}${hydrant.photoPath}" alt="Foto idrante" onerror="this.style.display='none'">` : ''}

<h2>Controllo</h2>
<table>
  <tr><th>Data ultimo controllo</th><td>${lastCheck}</td></tr>
  <tr><th>Compilatore</th><td>${safe(form.compilerName)}</td></tr>
</table>

<h2>Note generali</h2>
<p>${safe(form.notes) || '—'}</p>

<div class="footer">Generato il ${new Date().toLocaleString('it-IT')} — VVF Tracking Interventi</div>
<script>setTimeout(() => window.print(), 400)</script>
</body></html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }

  const TriSelect = ({ value, onChange, labelTrue, labelFalse }: {
    value: TriBool; onChange: (v: TriBool) => void; labelTrue: string; labelFalse: string;
  }) => (
    <select value={value} onChange={e => onChange(e.target.value as TriBool)} style={{ width: '100%', padding: 8 }}>
      <option value="">— non specificato —</option>
      <option value="true">{labelTrue}</option>
      <option value="false">{labelFalse}</option>
    </select>
  );

  const lastCheckLabel = hydrant.lastInspectionAt
    ? new Date(hydrant.lastInspectionAt).toLocaleDateString('it-IT')
    : hydrant.createdAt ? new Date(hydrant.createdAt).toLocaleDateString('it-IT') : '—';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}
        style={{ maxWidth: 720, maxHeight: '90vh', overflowY: 'auto' }}>
        <h3>📋 Scheda Idrante</h3>
        <div style={{ display: 'grid', gap: 10 }}>
          <fieldset style={fs}>
            <legend style={lg}>Localizzazione</legend>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <label>Codice idrante
                <input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} style={ip} />
              </label>
              <label>Comune
                <input value={form.municipality} onChange={e => setForm({ ...form, municipality: e.target.value })} style={ip} />
              </label>
              <label>Via
                <input value={form.street} onChange={e => setForm({ ...form, street: e.target.value })} style={ip} />
              </label>
              <label>N. civico
                <input value={form.civicNumber} onChange={e => setForm({ ...form, civicNumber: e.target.value })} style={ip} />
              </label>
              <label>Latitudine (auto)
                <input value={hydrant.latitude.toFixed(6)} readOnly style={{ ...ip, background: '#f3f3f3' }} />
              </label>
              <label>Longitudine (auto)
                <input value={hydrant.longitude.toFixed(6)} readOnly style={{ ...ip, background: '#f3f3f3' }} />
              </label>
            </div>
          </fieldset>

          <fieldset style={fs}>
            <legend style={lg}>Caratteristiche</legend>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <label>Tipo idrante
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={ip}>
                  <option value="Soprasuolo">Soprasuolo</option>
                  <option value="Sotterraneo">Sotterraneo</option>
                  <option value="Colonnina">Colonnina</option>
                  <option value="Altro">Altro</option>
                </select>
              </label>
              <label>Diametro UNI
                <input type="number" value={form.uniDiameter}
                  onChange={e => setForm({ ...form, uniDiameter: e.target.value })} style={ip} />
              </label>
              <label>Diametro (mm)
                <input type="number" value={form.diameterMm}
                  onChange={e => setForm({ ...form, diameterMm: e.target.value })} style={ip} />
              </label>
              <label>Pressione (bar)
                <input type="number" step="0.1" value={form.pressureBar}
                  onChange={e => setForm({ ...form, pressureBar: e.target.value })} style={ip} />
              </label>
              <label>Risorsa
                <select value={form.resource} onChange={e => setForm({ ...form, resource: e.target.value })} style={ip}>
                  <option value="">— seleziona —</option>
                  <option value="Pubblica">Pubblica</option>
                  <option value="Privata">Privata</option>
                </select>
              </label>
            </div>
          </fieldset>

          <fieldset style={fs}>
            <legend style={lg}>Stato</legend>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <label>Stato efficienza
                <TriSelect value={form.efficient} onChange={v => setForm({ ...form, efficient: v })}
                  labelTrue="Funzionante" labelFalse="Non funzionante" />
              </label>
              <label>Stato visivo
                <TriSelect value={form.visualGood} onChange={v => setForm({ ...form, visualGood: v })}
                  labelTrue="Buono" labelFalse="Non buono" />
              </label>
              <label>Valvola di chiusura
                <TriSelect value={form.valvePresent} onChange={v => setForm({ ...form, valvePresent: v })}
                  labelTrue="Presente" labelFalse="Non presente" />
              </label>
              <label>Cartello segnaletico
                <TriSelect value={form.signagePresent} onChange={v => setForm({ ...form, signagePresent: v })}
                  labelTrue="Presente" labelFalse="Non presente" />
              </label>
              <label style={{ gridColumn: '1 / -1' }}>Accessibilità mezzi pesanti APS/ABP (min 2,5mt)
                <TriSelect value={form.accessibilityGood} onChange={v => setForm({ ...form, accessibilityGood: v })}
                  labelTrue="Buona" labelFalse="Non buona" />
              </label>
              <label style={{ gridColumn: '1 / -1' }}>Note accessibilità
                <textarea value={form.accessibilityNotes}
                  onChange={e => setForm({ ...form, accessibilityNotes: e.target.value })}
                  style={{ ...ip, minHeight: 40 }} />
              </label>
              <label>Fotografia
                <TriSelect value={form.photoTaken} onChange={v => setForm({ ...form, photoTaken: v })}
                  labelTrue="Eseguita" labelFalse="Non eseguita" />
              </label>
            </div>
          </fieldset>

          <fieldset style={fs}>
            <legend style={lg}>Controllo</legend>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <label>Data ultimo controllo (auto)
                <input value={lastCheckLabel} readOnly style={{ ...ip, background: '#f3f3f3' }} />
              </label>
              <label>Compilatore
                <input value={form.compilerName}
                  onChange={e => setForm({ ...form, compilerName: e.target.value })} style={ip} />
              </label>
            </div>
          </fieldset>

          <label>Note generali
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
              style={{ ...ip, minHeight: 60 }} />
          </label>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14, flexWrap: 'wrap' }}>
          <button onClick={onClose} className="btn-secondary">Annulla</button>
          <button onClick={exportPdf}
            style={{ padding: '8px 16px', border: 'none', borderRadius: 6, cursor: 'pointer',
                     background: '#6f42c1', color: 'white', fontWeight: 700 }}>
            📄 Esporta PDF
          </button>
          <button onClick={() => save.mutate()} disabled={save.isPending}
            style={{ padding: '8px 16px', border: 'none', borderRadius: 6, cursor: 'pointer',
                     background: '#28a745', color: 'white', fontWeight: 700 }}>
            💾 Salva
          </button>
        </div>
      </div>
    </div>
  );
}

const fs: React.CSSProperties = { border: '1px solid #e0e0e0', borderRadius: 6, padding: 10 };
const lg: React.CSSProperties = { fontWeight: 700, color: '#dc3545', padding: '0 6px' };
const ip: React.CSSProperties = { width: '100%', padding: 8, boxSizing: 'border-box' };
